import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, logger } from "../lib/api";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext(null);
export const useWishlist = () => useContext(WishlistContext);

const loadLocal = () => {
  try {
    return JSON.parse(localStorage.getItem("capra_wishlist")) || [];
  } catch {
    return [];
  }
};

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState(loadLocal);

  useEffect(() => {
    localStorage.setItem("capra_wishlist", JSON.stringify(ids));
  }, [ids]);

  useEffect(() => {
    if (!user) return;
    const sync = async () => {
      const local = loadLocal();
      for (const id of local) {
        try {
          await api.post("/wishlist", { product_id: id });
        } catch (err) {
          logger.error(`Wishlist sync failed for ${id}`, err);
        }
      }
      try {
        const { data } = await api.get("/wishlist");
        setIds(data.map((p) => p.id));
      } catch (err) {
        logger.error("Wishlist fetch failed", err);
      }
    };
    sync();
  }, [user]);

  const toggle = useCallback(
    async (productId) => {
      const had = ids.includes(productId);
      setIds((prev) => (had ? prev.filter((i) => i !== productId) : [...prev, productId]));
      if (user) {
        try {
          if (had) await api.delete(`/wishlist/${productId}`);
          else await api.post("/wishlist", { product_id: productId });
        } catch (err) {
          logger.error("Wishlist server update failed", err);
        }
      }
      return !had;
    },
    [ids, user]
  );

  const has = useCallback((productId) => ids.includes(productId), [ids]);

  const value = useMemo(() => ({ ids, toggle, has }), [ids, toggle, has]);

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};
